import React, { useEffect, useRef, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { observer } from 'mobx-react';
import classNames from 'classnames';
import useDeviceDetect from 'Hooks/useDeviceDetect';
import { appConfigStore } from 'Models/AppConfigStore';
import { feeType } from 'Models/Enums';
import { store } from 'Models/Store';
import { whiteLabelStore } from 'Models/WhiteLabelStore';
import { getFerryBookingTransactionIdFromStorage } from 'Services/Api/_HumanWritten/BookingService/BookingService';
import { TransactionPricingResult } from 'Services/Api/_HumanWritten/PricingService/PricingResult';
import { formatPriceDisplay } from 'Util/_HumanWritten/PriceFormattingUtils';
import { setWizardData } from '../../../BookingWizardDataStore';
import { BookingWizardData } from '../../../BookingWizardData/BaseBookingWizardData';
import { useBookingWizardPricingCacheStore } from '../../../BookingWizardPricingCacheStore';
import { AddAnotherBookingButton } from '../AddAnotherBookingButton';
import { AddServiceFeeButton } from '../AddServiceFeeButton';
import AddedFeeLines from '../CartLineItems/AddedFeeLines';
import { CartCalculationLineItem } from '../CartLineItems/CartCalculationLineItem';
import CreditCardFeeLine from '../CartLineItems/CreditCardFeeLine';
import DiscountLines from '../CartLineItems/DiscountLines';
import GiftUsageLines from '../CartLineItems/GiftUsageLines';
import FerryPromoGiftCard from '../FerryPromoGiftCard';
import { summaryTotalFigures } from '../summaryTotalFigures';

export interface ClassBookingSummaryTotalProps {
	transactionPricingResults?: TransactionPricingResult;
	/** The wizard's sidebar (4a), the cart (6a / 6b), or the booking success page (5a / 8b). */
	location: 'sidebar' | 'cart' | 'booking-success' | 'check-in';
	wizardData?: BookingWizardData;
	onClearGiftCertificate?: (id: string) => void;
	onRemoveDiscount?: (discountId: string) => void;
	onClearFee?: (feeId: string) => void;
	onRemoveManagerDiscount?: () => void;
	onRemoveFee?: (feeType: feeType, departureDiscountAmount?: number, returnDiscountAmount?: number) => void;
	refetch?: boolean;
	setRefetch?: (refetch: boolean) => void;
	/**
	 * Refreshes the totals and resolves once they are on screen.
	 *
	 * <p>Supplied by the cart and by the check-in review, which each own the fetch. Optional because
	 * the summary is also rendered where nothing can be applied (the success page, the sidebar);
	 * without it the voucher field falls back to toggling `refetch`, which refreshes but cannot be
	 * waited on.</p>
	 */
	onVoucherApplied?: () => Promise<unknown>;
	bookingIds?: string[];
	/**
	 * Pay later splits a cart into a transaction per booking, and each carries its own fees. The
	 * success page then shows one combined figure rather than a breakdown, as the current summary does.
	 */
	combinedTotal?: { total: number; gst: number };
}

/**
 * The totals card for the fare-type summary. The figures are the ones `TripBookingSummaryTotal`
 * prints, from the same `summaryTotalFigures`; only the layout differs. On a phone and on the success
 * page the total leads (8b, 6b); in the web cart it closes the card (6a).
 */
function ClassBookingSummaryTotal({
	transactionPricingResults,
	location,
	wizardData,
	onClearGiftCertificate,
	onRemoveDiscount,
	onRemoveManagerDiscount,
	onRemoveFee,
	onClearFee,
	refetch,
	onVoucherApplied,
	setRefetch,
	bookingIds,
	combinedTotal,
}: ClassBookingSummaryTotalProps) {
	const { isMobileBookingWizard } = useDeviceDetect();
	const { getTotalBookingPrice } = useBookingWizardPricingCacheStore();
	const transactionId = getFerryBookingTransactionIdFromStorage() ?? undefined;
	const { gstLabel } = appConfigStore;
	const { gstPercentage } = whiteLabelStore.config;

	const isAlteration = transactionPricingResults?.isAlteration ?? false;
	const afterPayment = location === 'booking-success';

	// The voucher input keeps its `Voucher applied` message until the booker types again, so removing
	// the voucher with its cross left the message claiming one was still on. Remount the input when a
	// voucher or discount goes, which clears it; an apply is left alone so its message still shows.
	const voucherCount =
		transactionPricingResults?.lines?.filter(x => x.key === 'GiftCertificate' || x.key === 'Discount').length ?? 0;
	const previousVoucherCount = useRef(voucherCount);
	const [voucherInputKey, setVoucherInputKey] = useState(0);
	useEffect(() => {
		if (voucherCount < previousVoucherCount.current) {
			setVoucherInputKey(key => key + 1);
		}
		previousVoucherCount.current = voucherCount;
	}, [voucherCount]);

	const {
		totalAlterationFee,
		totalTrailerFee,
		totalCheckInForfeitFee,
		totalRemovalFee,
		checkInForfeitFeeLabel,
		removalFeeLabel,
		departurePriceIncrease,
		returnPriceIncrease,
		basePriceIncrease,
		gst,
		parkingPriceForSubtotal,
		feesListedBelowSubtotal,
	} = summaryTotalFigures(transactionPricingResults, wizardData, store.isStaff);

	const gstIncluded =
		gstPercentage > 0
			? `${transactionPricingResults?.lines?.find(x => x.key === 'Gst')?.description ?? gstLabel} included`
			: undefined;

	if (location === 'sidebar') {
		return (
			<div className="class-booking-summary__sidebar-total" data-test-id="class-booking-summary-sidebar-total">
				<div className="class-booking-summary__sidebar-total-row">
					<span>
						<FormattedMessage id="class-booking-summary__total" defaultMessage="Total" />
					</span>
					<span className="class-booking-summary__sidebar-total-price">
						{formatPriceDisplay(
							(transactionPricingResults?.total ?? 0) +
								getTotalBookingPrice(bookingIds, wizardData?.bookingToEdit),
							isAlteration,
						)}
					</span>
				</div>
				{gstIncluded && <div className="class-booking-summary__muted">{gstIncluded}</div>}
			</div>
		);
	}

	const total = combinedTotal?.total ?? transactionPricingResults?.total ?? 0;
	const totalGst = combinedTotal?.gst ?? gst;
	const totalFirst = afterPayment || isMobileBookingWizard;

	const totalRow = (
		<div
			className={classNames('class-booking-summary__total-row', {
				'class-booking-summary__total-row--first': totalFirst,
				'class-booking-summary__total-row--last': !totalFirst,
			})}
			data-test-id="class-booking-summary-total"
		>
			<div>
				<div className="class-booking-summary__total-label">
					{afterPayment ? (
						<FormattedMessage id="class-booking-summary__total-paid" defaultMessage="Total paid" />
					) : (
						<FormattedMessage id="class-booking-summary__total" defaultMessage="Total" />
					)}
				</div>
				{gstIncluded && <div className="class-booking-summary__muted">{gstIncluded}</div>}
			</div>
			<div className="class-booking-summary__total-amounts">
				<span className="class-booking-summary__total-price">{formatPriceDisplay(total, isAlteration)}</span>
				{gstIncluded && totalGst > 0 && (
					<span className="class-booking-summary__muted">{formatPriceDisplay(totalGst, false, true)}</span>
				)}
			</div>
		</div>
	);

	if (combinedTotal) {
		return (
			<div className="class-booking-summary__totals">
				<div className="class-booking-summary__totals-card">
					<div className="class-booking-summary__totals-body">{totalRow}</div>
				</div>
			</div>
		);
	}

	// The same capabilities the old summary card and the endpoints behind these crosses ask, not the
	// manager tier: a manager can have one taken away, and staff below manager can be granted it.
	const canWaiveFees = store.can('WaiveFees');
	const canApplyManagerDiscount = store.can('ApplyManagerDiscount');
	const waivable = canWaiveFees && !afterPayment;
	const discountRemovable = canApplyManagerDiscount && !afterPayment;

	return (
		<div className="class-booking-summary__totals">
			<div className="class-booking-summary__totals-card">
				<div className="class-booking-summary__totals-body">
					{totalFirst && totalRow}
					<CartCalculationLineItem
						keyToUse="subtotal-line-item-key"
						lineItemDisplayName="Subtotal"
						lineItemPrice={formatPriceDisplay(
							(transactionPricingResults?.subtotal ?? 0) +
								parkingPriceForSubtotal -
								feesListedBelowSubtotal,
							isAlteration,
						)}
						className="subtotal-line-item"
					/>
					<AddedFeeLines
						fees={transactionPricingResults?.lines?.filter(x => x.key === 'ServiceFee') ?? []}
						onRemoveFee={onClearFee}
					/>
					{store.isStaff && totalAlterationFee > 0 && (
						<CartCalculationLineItem
							lineItemDisplayName="Alteration fee"
							lineItemPrice={formatPriceDisplay(totalAlterationFee, false)}
							hasRemovalButton={waivable}
							removalOnClick={() => {
								if (canWaiveFees && onRemoveFee) {
									onRemoveFee('ALTERATION');
								}
							}}
						/>
					)}
					{totalTrailerFee > 0 && (
						<CartCalculationLineItem
							lineItemDisplayName="Trailer removal fee:"
							lineItemPrice={formatPriceDisplay(totalTrailerFee, false)}
							hasRemovalButton={waivable}
							removalOnClick={() => {
								if (canWaiveFees && onRemoveFee) {
									onRemoveFee('CANCELLATION');
								}
							}}
						/>
					)}
					{/* Shown to customers too, and with no cross: there is no server-side clear for it yet. */}
					{totalRemovalFee > 0 && (
						<CartCalculationLineItem
							lineItemDisplayName={removalFeeLabel || 'Removal fee'}
							lineItemPrice={formatPriceDisplay(totalRemovalFee, false)}
						/>
					)}
					{totalCheckInForfeitFee > 0 && (
						<CartCalculationLineItem
							lineItemDisplayName={checkInForfeitFeeLabel || 'Check-in forfeit fee'}
							lineItemPrice={formatPriceDisplay(totalCheckInForfeitFee, false)}
							hasRemovalButton={waivable}
							removalOnClick={() => {
								if (canWaiveFees && onRemoveFee) {
									onRemoveFee('CHECK_IN_FORFEIT');
								}
							}}
						/>
					)}
					{canApplyManagerDiscount && transactionPricingResults?.lines?.some(x => x.key === 'ManagerDiscount') && (
						<CartCalculationLineItem
							lineItemDisplayName="Manager discount:"
							lineItemPrice={formatPriceDisplay(
								transactionPricingResults?.lines?.find(x => x.key === 'ManagerDiscount')?.price ?? 0,
								isAlteration,
							)}
							hasRemovalButton={discountRemovable}
							removalOnClick={() => {
								if (canApplyManagerDiscount && onRemoveManagerDiscount) {
									onRemoveManagerDiscount();
								}
							}}
						/>
					)}
					{canApplyManagerDiscount &&
						basePriceIncrease > 0 &&
						!transactionPricingResults?.lines?.some(x => x.key === 'ManagerDiscount') && (
							<CartCalculationLineItem
								lineItemDisplayName="Ticket price increase"
								lineItemPrice={formatPriceDisplay(basePriceIncrease, false)}
								hasRemovalButton={discountRemovable}
								removalOnClick={() => {
									// 'OTHER' is not a fee removal: CartSummary turns it into an add-manager-discount.
									if (canApplyManagerDiscount && onRemoveFee) {
										onRemoveFee('OTHER', departurePriceIncrease, returnPriceIncrease);
									}
								}}
							/>
						)}
					<DiscountLines
						discounts={transactionPricingResults?.lines?.filter(x => x.key === 'Discount') ?? []}
						onClearDiscount={onRemoveDiscount}
					/>
					<GiftUsageLines
						giftCertificateUsages={
							transactionPricingResults?.lines?.filter(x => x.key === 'GiftCertificate') ?? []
						}
						onClearGiftCertificate={onClearGiftCertificate}
					/>
					<CreditCardFeeLine
						lineItem={transactionPricingResults?.lines?.find(x => x.key === 'CreditCard') ?? null}
						gstLabel={gstLabel}
					/>
					{!totalFirst && totalRow}
					{/* Staff add service fees at the gate as well as in the cart, as the old summary let them.
					    Asks the capability add-fee-to-transaction carries, not the tier, as the old summary does. */}
					{store.can('ManageServiceFees') && (location === 'cart' || location === 'check-in') && (
						<AddServiceFeeButton
							transactionPricingResults={transactionPricingResults}
							tripSummaryLocation={location}
							wizardMode={wizardData?.wizardMode ?? 'CREATE'}
							transactionId={transactionId}
							refetch={refetch}
							setRefetch={setRefetch}
						/>
					)}
				</div>
				{/* 7e / 7g: the voucher field is part of the totals card, on a tinted band beneath them. */}
				{!afterPayment && (
					<div className="class-booking-summary__voucher">
						<FerryPromoGiftCard
							key={voucherInputKey}
							transactionId={transactionId ?? ''}
							onAddGiftCertificate={async () => {
								// Awaited where the parent gave us something to wait for, so the
								// voucher field keeps saying "Applying voucher" until the totals
								// beside it have actually changed.
								if (onVoucherApplied) {
									await onVoucherApplied();
									return;
								}
								if (setRefetch) {
									setRefetch(!refetch);
								}
							}}
							forCheckIn={location === 'check-in'}
						/>
					</div>
				)}
			</div>
			{location === 'cart' && (
				<AddAnotherBookingButton
					wizardData={wizardData}
					onUpdateWizardData={setWizardData}
					redirectURL={isMobileBookingWizard ? '/booking-wizard/search' : '/booking-wizard/tickets'}
					className="class-booking-summary__add-another"
				/>
			)}
		</div>
	);
}

export default observer(ClassBookingSummaryTotal);
